import { useCallback, useMemo } from 'react';
import { useRMDispatch } from 'redux/hooks';
import { useModalHandlers } from 'components/modal';
import { setAutoScoreIgnoredStatusThunk } from 'redux/thunks/experiment';
import { toggleScoringWell } from 'redux/reducers/experimentDataReducer';
import {
  getIgnoreAutoScoreConfirmationMessages,
  getIgnoreAutoScoreSuccessModalMessages,
} from 'constants/messages/ignoreAutoScore';
import { IScoresOfWellDropsDTO } from 'business/models/Models';
import { ScoresOfWellDrops } from 'components/Experiment/constants';

export type IgnoreAutoScoreParams = {
  plateId: number;
  imageBatchId: number;
  wellNumber: number;
  dropNumber: number;
  dropId?: number;
};

type WellDropScores =
  | ScoresOfWellDrops[][]
  | ScoresOfWellDrops[]
  | IScoresOfWellDropsDTO[];

function flattenScores(scores?: WellDropScores): IScoresOfWellDropsDTO[] {
  if (!scores?.length) {
    return [];
  }

  return (Array.isArray(scores[0]) ? scores.flat() : scores) as IScoresOfWellDropsDTO[];
}

export function hasIgnoredAutoScore(scores?: WellDropScores): boolean {
  return flattenScores(scores).some(
    (score) => score.isAutoScore === true && score.isAutoScoreIgnored === true,
  );
}

export const useIgnoreAutoScoreWellDrop = ({
  scores,
  params,
  isDisabled = false,
  refreshExperimentScores = false,
  onToggleStart,
  onToggleEnd,
}: {
  scores?: WellDropScores;
  params?: IgnoreAutoScoreParams | null;
  isDisabled?: boolean;
  refreshExperimentScores?: boolean;
  onToggleStart?: () => void;
  onToggleEnd?: () => void;
}) => {
  const dispatch = useRMDispatch();
  const { showModal, hideModal } = useModalHandlers();

  const scoresForCurrentDrop = useMemo(() => {
    const flatScores = flattenScores(scores);
    if (!params) {
      return flatScores;
    }

    return flatScores.filter(
      (score) =>
        score.dropNumber === params.dropNumber &&
        score.scoreBatchId === params.imageBatchId,
    );
  }, [scores, params?.dropNumber, params?.imageBatchId]);

  const isAutoScoreIgnored = useMemo(
    () => hasIgnoredAutoScore(scoresForCurrentDrop),
    [scoresForCurrentDrop],
  );
  const isIgnoreAutoScoreDisabled = isDisabled || !params;

  const toggleAutoScoreIgnoredStatus = useCallback(async () => {
    if (!params) {
      return;
    }

    onToggleStart?.();
    const shouldIgnoreAutoScore = !isAutoScoreIgnored;

    try {
      const success = await dispatch(
        setAutoScoreIgnoredStatusThunk({
          ...params,
          isAutoScoreIgnored: shouldIgnoreAutoScore,
        }),
      );

      if (success) {
        if (refreshExperimentScores) {
          dispatch(toggleScoringWell());
        }

        showModal({
          ...getIgnoreAutoScoreSuccessModalMessages(shouldIgnoreAutoScore),
          onHide: hideModal,
        });
      }
    } finally {
      onToggleEnd?.();
    }
  }, [
    dispatch,
    hideModal,
    isAutoScoreIgnored,
    onToggleEnd,
    onToggleStart,
    params,
    refreshExperimentScores,
    showModal,
  ]);

  const showIgnoreAutoScoreConfirmation = useCallback(() => {
    showModal({
      ...getIgnoreAutoScoreConfirmationMessages(!isAutoScoreIgnored),
      onCancel: hideModal,
      onOk: () => {
        hideModal();
        toggleAutoScoreIgnoredStatus();
      },
    });
  }, [hideModal, isAutoScoreIgnored, showModal, toggleAutoScoreIgnoredStatus]);

  return {
    showIgnoreAutoScoreConfirmation,
    isAutoScoreIgnored,
    isIgnoreAutoScoreDisabled,
  };
};
